import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { CheckCircle2, MapPin, Phone, Mail, ArrowLeft, Camera, Mic, Lightbulb, Video, Palette, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import ContactModal from '../components/ContactModal';
import axios from 'axios';
import usePageTracking from '../hooks/usePageTracking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MiamiComingSoon = () => {
  usePageTracking('miami');
  const navigate = useNavigate();
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    contentType: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleWhatsApp = () => {
    window.open('https://wa.me/447919245522', '_blank');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/analytics/waitlist`, {
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        content_type: formData.contentType
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Error joining waitlist:', err);
      alert('Failed to join waitlist. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white">
        <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
            <div className="text-2xl font-bold tracking-tight cursor-pointer" onClick={() => navigate('/')}>LTV Production</div>
          </div>
        </header>
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="text-center max-w-2xl mx-auto px-6">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-black rounded-full mb-6">
              <CheckCircle2 className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-5xl font-bold mb-4">You're on the list ✅</h1>
            <p className="text-xl text-gray-600 mb-8">
              Thanks for joining the LTV Studio Miami waitlist. We'll email you as soon as early access booking opens.
            </p>
            <Button size="lg" onClick={() => navigate('/')} className="bg-black hover:bg-gray-800 text-white">
              Back to Home
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const features = [
    { icon: Camera, text: 'Multi-camera filming options' },
    { icon: Lightbulb, text: 'Professional lighting setups' },
    { icon: Mic, text: 'Clean studio sound and microphone options' },
    { icon: Palette, text: 'High-quality set design / premium background options' },
    { icon: Users, text: 'On-site production support' },
    { icon: Video, text: 'Full post-production (editing, sound, colour grading)' }
  ];

  const benefits = [
    'Priority booking access',
    'Launch date announcements',
    'Early-bird studio packages',
    'Exclusive opening promotions'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_d70cc816-e4d3-4f6e-bfaa-57b96c01f1a7/artifacts/w1f3hxo7_LTV-Production-LOGO-%281%29-white-2.png" 
            alt="LTV Production" 
            className="h-8 w-auto cursor-pointer"
            style={{ filter: 'invert(1)' }}
            onClick={() => navigate('/')}
          />
          <Button variant="ghost" onClick={() => navigate('/')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-gray-50 via-white to-gray-100">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <Badge className="mb-6 bg-black text-white text-lg px-6 py-3">COMING SOON</Badge>
          <h1 className="text-6xl md:text-7xl font-bold mb-6">LTV Studio Miami</h1>
          <p className="text-2xl text-gray-700 mb-4">
            A premium studio space for podcast and video production is launching soon in Miami.
          </p>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8 leading-relaxed">
            Built for creators, brands, and international clients looking for high-end content production with a clean, cinematic finish.
          </p>
          <div className="flex items-center justify-center gap-2 text-gray-600">
            <MapPin className="h-5 w-5" />
            <span>Miami, Florida</span>
            <span className="mx-2">•</span>
            <span>Launching soon</span>
            <span className="mx-2">•</span>
            <span>Limited early access slots</span>
          </div>
        </div>
      </section>

      {/* What's Coming */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-5xl font-bold mb-6 text-center">A New Studio Experience in Miami</h2>
          <p className="text-xl text-gray-600 leading-relaxed text-center max-w-3xl mx-auto">
            LTV Studio Miami is designed to deliver a luxury studio production experience in the USA — combining clean visuals, professional sound, and a production workflow that makes content look broadcast-ready. Whether you're launching a podcast, filming interviews, or creating branded video content, our Miami location will offer the same premium quality you expect from LTV.
          </p>
        </div>
      </section>

      {/* Perfect For */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-5xl font-bold mb-12 text-center">Perfect For</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              'Podcast Recording (Audio + Video)',
              'YouTube Shows & Talk Shows',
              'TV-Style Interviews',
              'Content Creators & Influencers',
              'Brand Campaigns & Product Features',
              'Corporate Interviews & Media',
              'Visiting Talent / International Clients'
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-4 p-4 bg-white rounded-lg border-2 border-gray-200">
                <CheckCircle2 className="h-6 w-6 text-black flex-shrink-0" />
                <span className="text-lg font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Studio Features */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-5xl font-bold mb-12 text-center">Studio Features (Preview)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <p className="text-lg pt-2">{feature.text}</p>
                </div>
              );
            })}
          </div>
          <p className="text-center text-gray-600 mt-8 italic">
            Final studio features and set options will be announced before launch.
          </p>
        </div>
      </section>

      {/* Waitlist Form */}
      <section className="py-24 bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-5xl font-bold mb-6">Join the Waitlist for Early Access</h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Waitlist members will get first priority on launch dates, early booking slots, and exclusive opening offers.
              </p>
              <div className="space-y-3">
                <h3 className="text-2xl font-bold mb-4">Waitlist Benefits:</h3>
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-white" />
                    <span className="text-lg">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            <Card className="border-0 shadow-2xl">
              <CardHeader>
                <CardTitle className="text-2xl">Get Notified When Miami Launches</CardTitle>
                <CardDescription>Join our exclusive waitlist for early access</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="fullName">Full Name *</Label>
                    <Input
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      className="mt-2"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="mt-2"
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number (optional)</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="mt-2"
                      placeholder="+1 (234) 567-8900"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contentType">Type of Content</Label>
                    <select
                      id="contentType"
                      name="contentType"
                      value={formData.contentType}
                      onChange={handleChange}
                      className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Select...</option>
                      <option value="podcast">Podcast</option>
                      <option value="interview">Interview</option>
                      <option value="brand">Brand</option>
                      <option value="film">Film</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <Button type="submit" size="lg" className="w-full bg-black hover:bg-gray-800 text-white" disabled={loading}>
                    {loading ? 'Joining...' : 'Join the Waitlist'}
                  </Button>
                  <p className="text-xs text-gray-500 text-center">
                    We'll only email you about LTV Studio Miami launch updates. No spam.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Need Filming Now */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-5xl font-bold mb-6">Need Filming Now? We're Ready.</h2>
          <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed">
            If you need to film immediately, you can book our London studio today or use our on-location production service — we'll bring the full production setup to your location anywhere in the world.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-black hover:bg-gray-800 text-white"
              onClick={() => window.open('https://www.ltvstudio.co.uk', '_blank')}
            >
              Book London Studio
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-2 border-black hover:bg-black hover:text-white"
              onClick={() => navigate('/on-location')}
            >
              On-Location Production
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Block */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Business Enquiries</h2>
          <p className="text-xl text-gray-600 mb-8">
            For partnership enquiries, brand collaborations, or advanced production planning in Miami, contact the team directly.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => setContactModalOpen(true)} className="bg-black hover:bg-gray-800 text-white">
              <Mail className="mr-2 h-5 w-5" />
              Contact Us
            </Button>
            <Button size="lg" onClick={handleWhatsApp} variant="outline" className="border-2 border-black hover:bg-black hover:text-white">
              <Phone className="mr-2 h-5 w-5" />
              WhatsApp Us
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <ContactModal isOpen={contactModalOpen} onClose={() => setContactModalOpen(false)} />
    </div>
  );
};

export default MiamiComingSoon;